import React from 'react';

// Who is allowed to touch the two refs that decide what a landing read MEANS.
//
// The same preamble was retyped before every `run()` — six call sites, two refs each — and nothing
// enforced which flag value belonged to which site:
//
//   pendingClearStackRef.current = true;   // or false
//   pendingRunRef.current = params;        // or null
//   run({ ... });
//
// Those two flags are exactly what decide whether the drill-down trail survives the read and
// whether it joins Recent runs. Neither throws when wrong. `pendingClearStack = true` on a re-read
// wipes the breadcrumb out from under rows still on screen; `pendingRun` set on a navigation read
// pollutes Recent runs with member-feed descents. Both are invisible until an analyst is three
// levels deep, and any NEW affordance that issues a read — a refresh, a saved view, a preset chip —
// was a seventh copy waiting to get one of them wrong.
//
// The kind decides now, in one switch.
export const READ_KINDS = Object.freeze({
  // The analyst asked a new question: Submit, or pressing a listed run. Replaces the drill-down path
  // once the ROWS land, and joins Recent runs once they do.
  TOP_LEVEL: 'TOP_LEVEL',
  // Moving within the feed graph: descending into a member, or the one bootstrap read a member may
  // spend. Not a query worth listing, and the path it is building must survive it.
  NAVIGATION: 'NAVIGATION',
  // The same rows again, asked for differently — the columns notice, the load-columns button, the
  // automatic index re-read. Not a query, and the path standing over these rows stays put.
  RE_READ: 'RE_READ',
  // A local fixture. Replaces the path like a top-level read, but records nothing: a mock key names
  // no feed the server has heard of, so it could never be re-run from the list.
  FIXTURE: 'FIXTURE',
});

// TOP_LEVEL and FIXTURE both replace the path; only TOP_LEVEL is a query worth recalling.
// NAVIGATION and RE_READ write the same two values today and are still kept apart, because they are
// different facts — a change to what a navigation read does to the path must not silently become a
// change to what a re-read does.
const CLEARS_PATH = { TOP_LEVEL: true, FIXTURE: true, NAVIGATION: false, RE_READ: false };
const RECORDS_RUN = { TOP_LEVEL: true, FIXTURE: false, NAVIGATION: false, RE_READ: false };

export const useFeedReads = ({ run, recordRun, result }) => {
  // The params of a read worth listing, held until its result lands so the recorded entry can carry
  // the row count. Null for everything else.
  const pendingRunRef = React.useRef(null);

  // A top-level read replaces whatever path a drill-down took — but only once its ROWS arrive.
  // Clearing at submit time threw the path away while the rows it described were still on screen:
  // submit a bad feed id from three levels down and the trail vanished, taking with it the only copy
  // of every parent result, and leaving the analyst on a member feed with no way back up.
  const pendingClearStackRef = React.useRef(false);

  // Recorded on success, so a failed read never joins the list and the entry can carry its row count.
  React.useEffect(() => {
    if (!result || !pendingRunRef.current) return;
    recordRun(pendingRunRef.current, result);
    pendingRunRef.current = null;
  }, [result, recordRun]);

  // The one door. Returns the run promise UNTOUCHED — openMemberFeed branches on `!response` (a
  // newer read superseded this one) against `!response.rows` (it landed with nothing), and
  // swallowing or re-wrapping it would collapse two outcomes the drill-down has to tell apart.
  const issueRead = React.useCallback(
    ({ kind, params, request }) => {
      pendingClearStackRef.current = CLEARS_PATH[kind];
      pendingRunRef.current = RECORDS_RUN[kind] ? params : null;
      return run(request);
    },
    [run]
  );

  // A fixture goes through a different function, so it sets the flags and hands back rather than
  // issuing anything.
  const issueFixture = React.useCallback(() => {
    pendingClearStackRef.current = CLEARS_PATH[READ_KINDS.FIXTURE];
    pendingRunRef.current = null;
  }, []);

  // The two transitions that are not reads, spelled out rather than left as bare ref writes.

  // Back to first load: nothing is pending and no path is waiting to be replaced.
  const abandonReads = React.useCallback(() => {
    pendingRunRef.current = null;
    pendingClearStackRef.current = false;
  }, []);

  // Stepping back up the path restores rows rather than re-reading them, so a submit that never
  // landed must not get to wipe the path out from under what this puts back.
  const keepPath = React.useCallback(() => {
    pendingClearStackRef.current = false;
  }, []);

  // Read once, by the effect that files a landed result. Consuming it here is what keeps the flag
  // single-use without a second ref to say so.
  const consumeClearPath = React.useCallback(() => {
    if (!pendingClearStackRef.current) return false;
    pendingClearStackRef.current = false;
    return true;
  }, []);

  // Which listed run the in-flight read is for, so the row can say so while it runs. Reading a ref
  // in render is safe here for the reason the page documents at length: the render that reads it is
  // the one `isRunning` just caused, and re-rendering reaches the same value.
  const pendingRun = () => pendingRunRef.current;

  return { issueRead, issueFixture, abandonReads, keepPath, consumeClearPath, pendingRun };
};
